import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import api, { streamUrl } from "@/lib/api";
import { catLabel } from "@/lib/constants";
import { Zap, Share2, Flag, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import CastingInquiryModal from "@/components/CastingInquiryModal";
import SponsoredBanner from "@/components/SponsoredBanner";
import BackButton from "@/components/BackButton";

export default function VideoDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [error, setError] = useState("");
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    api.get(`/videos/${id}`).then(r => setV(r.data)).catch((e) => setError(e?.response?.data?.detail || "Not found"));
  }, [id]);

  if (error) return <div className="min-h-screen"><TopBar/><p className="p-6 text-zinc-400 text-sm">{error}</p></div>;
  if (!v) return <div className="min-h-screen"><TopBar/><p className="p-6 text-zinc-400 text-sm">Loading…</p></div>;

  const share = async () => {
    const url = `${window.location.origin}/v/${v.id}`;
    if (navigator.share) { try { await navigator.share({ title: v.title || "NextScene", url }); return; } catch {} }
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };
  const report = async () => {
    if (!confirm("Report this video?")) return;
    await api.post(`/videos/${v.id}/report`, { reason: "" });
    toast.success("Reported");
  };

  return (
    <div data-testid="video-detail-page" className="min-h-screen pb-24">
      <TopBar/>
      <div className="max-w-2xl mx-auto px-4 pt-4">
        <BackButton label="Back" className="mb-3"/>
        <SponsoredBanner placement="performance_detail" className="mb-3"/>
        <div className="rounded-2xl overflow-hidden border border-zinc-800 bg-black">
          <video data-testid="detail-video" src={streamUrl(v.storage_path)} controls playsInline className="w-full max-h-[70vh] object-contain bg-black"/>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <Link to={`/${v.username}`} className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-[#DC2626]/15 border border-[#DC2626]/40 flex items-center justify-center text-[#DC2626] font-semibold">
              {(v.name || "?").slice(0,1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate">{v.name}</p>
              <p className="text-xs text-zinc-400 truncate">@{v.username} · {catLabel(v.category)}</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button data-testid="detail-share" onClick={share} className="w-9 h-9 rounded-full border border-zinc-700 bg-zinc-900 text-white flex items-center justify-center"><Share2 size={16}/></button>
            <button data-testid="detail-report" onClick={report} className="w-9 h-9 rounded-full border border-zinc-700 bg-zinc-900 text-red-400 flex items-center justify-center"><Flag size={16}/></button>
          </div>
        </div>
        {v.title && <h1 className="mt-4 font-display text-2xl font-bold">{v.title}</h1>}
        {v.description && <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{v.description}</p>}
        <div className="mt-3 text-xs text-zinc-400 flex items-center gap-3">
          <span>{v.views || 0} views</span>
          <span>·</span>
          <span>{v.likes || 0} likes</span>
          <span>·</span>
          <span>{v.duration}s</span>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <Link to={`/app/upload?challenge=${v.id}`} data-testid="detail-challenge" className="inline-flex items-center gap-2 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-black font-semibold px-5 h-11 pulse-gold transition-colors">
            <Zap size={16}/> Challenge this
          </Link>
          <button data-testid="detail-cast" onClick={() => setInquiryOpen(true)} className="inline-flex items-center gap-2 rounded-full border-2 border-[#DC2626]/50 hover:bg-[#DC2626]/10 text-white font-semibold px-5 h-11 transition-colors">
            <MessageSquare size={16}/> Cast this talent
          </button>
        </div>

        <CastingInquiryModal open={inquiryOpen} target={{ username: v.username, video_id: v.id }} onClose={() => setInquiryOpen(false)}/>

        {v.challenge_responses?.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display text-lg font-semibold mb-3">Responses ({v.challenge_responses.length})</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {v.challenge_responses.map((r) => (
                <Link key={r.id} to={`/v/${r.id}`} data-testid={`response-${r.id}`} className="aspect-[9/16] rounded-xl overflow-hidden bg-black border border-zinc-800 relative">
                  <video src={streamUrl(r.storage_path)} muted playsInline preload="metadata" className="w-full h-full object-cover"/>
                  <div className="absolute bottom-1 left-1 right-1 text-xs text-white line-clamp-1">@{r.username}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
